import { AfterViewInit, Component, computed, effect, signal, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { from } from 'rxjs';

import { AccountPageService } from '../../../services/account-page.service';
import { AppService } from 'src/app/infrastructure/services/app-services/app.service';
import { ButtonActionComponent } from 'src/app/features/shared/controls/container/button-action/button-action.component';
import { DomainProfileService } from 'src/app/infrastructure/services/domain-profile-services/domain-profile.service';
import { FooterService } from 'src/app/features/shared/footer/footer.service';
import { HeaderService } from 'src/app/features/shared/header/services/header.service';
import { IFooterData } from 'src/app/features/shared/footer/models/footer-data';
import { IHeaderData } from 'src/app/features/shared/header/models/header-data';
import { ApiService } from 'src/app/infrastructure/services/api-service/api.service';
import { ImageBoxComponent } from 'src/app/features/shared/image-boxes/image-box/image-box.component';
import { InputControlComponent } from 'src/app/features/shared/controls/container/input-control/input-control.component';
import { IInvitationPublicDto, invitationPublicDto } from 'src/assets/common/models/invitation-public-dto.g';
import { invitationStatus } from 'src/assets/common/enums/invitation-status.g';
import { IRegisterDto } from 'src/assets/common/models/register-dto.g';
import { LoadingPageService } from 'src/app/infrastructure/services/loading-services/loading-page.service';
import { SharedModule } from 'src/app/features/shared/shared.module';
import { _CentralPageService } from 'src/app/features/shared/central-page/services/_central-page.service';
import { _PageService } from 'src/app/infrastructure/services/page-services/_page.service';
import { _SectionBaseComponent } from 'src/app/infrastructure/components/section-components/_section-base-component';
import { injectRequired } from 'src/app/infrastructure/functions/inject-required.function';
import { sectionLayout, TSectionLayout } from 'src/app/infrastructure/models/section-layout';
import { loadKey } from 'src/app/infrastructure/services/loading-services/models/loadKey';
import { routePage } from 'src/app/infrastructure/services/route-service/models/route-page';
import { ActivatedRouteService } from 'src/app/infrastructure/services/route-service/activated-route.service';
import { userDtoConstraints } from 'src/assets/common/models/user-dto.g';
import { validatorsFromConstraint } from 'src/app/infrastructure/functions/validators-from-constraint.function';
import { passwordMatchValidator, passwordValidators } from '../../../functions/password.validators';

type TRegisterViewState = 'form' | 'emailSent';

@Component({
    selector: 'app-register-details',
    templateUrl: './register-details.component.html',
    standalone: true,
    imports: [
        SharedModule,
        InputControlComponent,
        ButtonActionComponent,
        ImageBoxComponent,
    ],
    styleUrls: [
        '../../../account.scss',
        './register-details.component.scss',
    ]
})
export class RegisterDetailsComponent
    extends _SectionBaseComponent
    implements OnInit, AfterViewInit
{
    protected override readonly _centralPageService = injectRequired(_CentralPageService);
    protected override readonly _footerService = injectRequired(FooterService);
    protected override readonly _headerService = injectRequired(HeaderService);
    protected override readonly _loadingPageService = injectRequired(LoadingPageService);
    protected override readonly _pageService = injectRequired(_PageService);

    private readonly __formBuilder = injectRequired(UntypedFormBuilder);
    private readonly __accountPageService = injectRequired(AccountPageService);
    private readonly __activatedRouteService = injectRequired(ActivatedRouteService);
    private readonly __apiService = injectRequired(ApiService);
    private readonly __appService = injectRequired(AppService);
    private readonly __appProfileService = injectRequired(DomainProfileService);

    override out_onEscape = this._out_onEscape;
    override out_onPageLoaded = this._out_onPageLoaded;

    override readonly sectionLayout$$ = signal<TSectionLayout>(sectionLayout.form);

    override template_footerLeft$$ = this._template_footerLeft$$;
    override template_sectionDetails_extraButtons$$ = this._template_sectionDetails_extraButtons$$;
    override template_sectionEdit_extraButtons$$ = this._template_sectionEdit_extraButtons$$;
    override footerData: Partial<IFooterData> = {
        showInfoBlock: true,
        hideSideBlockLeft: true,
        hideSideBlockRight: true,
    };
    // sideBlockLeft_hide: false — spiegelt RegisterComponent (de omvattende page-route-component),
    // die het domainlogo in de header expliciet wil tonen. Login houdt dit bewust wél verborgen
    // (zie LoginComponent/LoginDetailsComponent), vandaar dat dit hier afwijkt.
    override headerData: Partial<IHeaderData> = {
        sideBlockLeft_hide: false,
        sideBlockRight_hide: true,
        title: this.__appProfileService.title$$() ?? this.__appService.title$$() ?? '',
        subTitle: this.__appProfileService.title_sub$$() ?? this.__appService.sutTitle$$() ?? '',
        subTitle_hide: false,
    };

    private __form: UntypedFormGroup = this.__formBuilder.group({
        // Zelfde regels als het account (userDtoConstraints): alle naamvelden optioneel (privacy:
        // technisch is alleen het e-mailadres nodig). FirstName/LastName = formele naam (facturatie),
        // applicationName = naam zichtbaar voor anderen; leeg → formele naam → e-mailadres.
        firstName: ['', validatorsFromConstraint(userDtoConstraints.firstName)],
        lastName: ['', validatorsFromConstraint(userDtoConstraints.lastName)],
        applicationName: ['', validatorsFromConstraint(userDtoConstraints.applicationName)],
        // Aanhef in mails; leeg → Name → e-mailadres (User.MailName in de backend).
        correspondenceName: ['', validatorsFromConstraint(userDtoConstraints.correspondenceName)],
        email: ['', [Validators.required, Validators.pattern('^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$')]],
        password: ['', passwordValidators],
        passwordRepeat: ['', Validators.required],
    }, { validators: passwordMatchValidator('password', 'passwordRepeat') });

    get Form() {return this.__form;}
    // Cast naar FormControl: UntypedFormGroup.controls levert AbstractControl, maar
    // app-input-control verwacht een FormControl-input (runtime zijn het FormControls).
    get FormControls() {return this.__form.controls as { [key: string]: FormControl };}

    // Na een geslaagde registratie flipt de sectie naar het "check je e-mail"-paneel;
    // geen aparte route nodig.
    private __viewState$$ = signal<TRegisterViewState>('form');
    viewState$$ = this.__viewState$$.asReadonly();
    showForm$$ = computed(() => this.viewState$$() === 'form');

    // Invitation-flow: token uit de uitnodigingsmail-link (?invitation=...), wordt
    // ongewijzigd meegestuurd in de register-DTO. De banner toont voor welk domain de
    // uitnodiging is; het e-mailveld wordt vergrendeld op het uitgenodigde adres
    // (server-side afgedwongen via de e-mail-match in Invitation_Accept).
    private __invitationToken: string | null = null;
    private __invitation$$ = signal<IInvitationPublicDto | null>(null);
    invitation$$ = this.__invitation$$.asReadonly();
    private __invitationInvalid$$ = signal<boolean>(false);
    invitationInvalid$$ = this.__invitationInvalid$$.asReadonly();

    invitationKeys = invitationPublicDto;
    loadKey = loadKey.loadingPage;
    logoPath$$ = computed(() => this.__appProfileService.logo_url$$());
    pageName_current = routePage.register;
    pageName_return = routePage.login;
    showErrors = false;

    constructor() {
        super();

        // Domain-0-fallback (titel/subtitel) kan async binnenkomen ná de eerste render —
        // reactief bijwerken, zelfde patroon als LoginDetailsComponent.
        effect(() => {
            const __title = this.__appProfileService.title$$() ?? this.__appService.title$$() ?? '';
            const __subTitle = this.__appProfileService.title_sub$$() ?? this.__appService.sutTitle$$() ?? '';
            this._headerService.headerData_set({ title: __title, subTitle: __subTitle });
        });

    }

    override ngOnInit() {
        super._ngOnInit();

        this.__invitationToken = this.__activatedRouteService.queryParams?.['invitation'] ?? null;
        if (this.__invitationToken) this.__invitation_load(this.__invitationToken);

        this._footerService.info_set(this.__appService?.version$$() ?? null);
        this._footerService.infoTooltip_set(this.__appService?.versionDetail$$() ?? null);
        this.emit_pageLoaded();
    }

    private __invitation_load(token: string): void {
        from(this.__apiService.GET('/api/Invitation/details', { params: { query: { token } } }))
            .subscribe({
                next: r => {
                    const __invitation = r.data as unknown as IInvitationPublicDto | undefined;

                    // Alleen een open, niet-verlopen uitnodiging prefillt en vergrendelt het
                    // e-mailveld; anders gewoon registreren zonder koppeling (token wordt
                    // gewist, anders laat de server de hele registratie falen).
                    if (__invitation && __invitation.status === invitationStatus.pending && !__invitation.isExpired) {
                        // Al een bestaand account met dit e-mailadres: registreren kan/hoeft niet
                        // (de backend zou een duplicate-e-mailfout geven) — de genodigde moet
                        // gewoon inloggen. Token blijft meegaan zodat login de uitnodiging na
                        // succesvol inloggen alsnog accepteert.
                        if (__invitation.hasAccount) {
                            this._pageService.page_goto(routePage.login, {}, null, { invitation: token });
                            return;
                        }

                        this.__invitation$$.set(__invitation);
                        this.FormControls.email.setValue(__invitation.email);
                        this.FormControls.email.disable();
                    } else {
                        this.__invitation_invalidate();
                    }
                },
                error: () => this.__invitation_invalidate(),
            });
    }

    private __invitation_invalidate(): void {
        this.__invitationToken = null;
        this.__invitationInvalid$$.set(true);
    }

    override emit_onEscape(): void {
        super._emit_onEscape();
    }

    override emit_pageLoaded(): void {
        super._emit_pageLoaded();
    }

    emit_gotoLogin(): void {
        this._pageService.page_goto(routePage.login);
    }

    register() {
        if (this.__form.invalid) {
            this.showErrors = true;
            return;
        }

        // getRawValue: het e-mailveld is disabled bij een invitation (prefilled+locked) en
        // disabled controls vallen buiten .value.
        const __dto: IRegisterDto = {
            email: this.__form.getRawValue().email,
            password: this.FormControls.password.value,
            applicationName: this.FormControls.applicationName.value || null,
            firstName: this.FormControls.firstName.value || null,
            lastName: this.FormControls.lastName.value || null,
            correspondenceName: this.FormControls.correspondenceName.value || null,
            invitationToken: this.__invitationToken,
        };

        this.__accountPageService.register$(__dto)
            .subscribe({
                next: () => this.__viewState$$.set('emailSent'),
                // Fout wordt centraal door de HTTP-interceptor gerapporteerd (incl. notificatie).
                error: () => { /* afgehandeld door ErrorReportingService */ },
            });
    }

    resendConfirm() {
        const __email: string = this.FormControls.email.value;
        if (!__email) return;

        this.__accountPageService.resendConfirm$(__email)
            .subscribe({
                error: () => { /* afgehandeld door ErrorReportingService */ },
            });
    }

    override ngAfterViewInit(): void {
        super._ngAfterViewInit();
    }
}
